
import { supabase } from '../../supabase';
import { toast } from 'sonner';
import { validatePaperFile, checkUploadRateLimit, verifyStorageBucket } from './validation';
import { getSecurityToken } from './token-service';
import { uploadFileToStorage, withRetry } from './storage-service';
import { adminError } from '@/lib/utils/admin-logger';

// Export validation helper for external use
export { validatePaperFile } from './validation';

// Export storage service functions for external use
export {
  uploadFileToStorage,
  getPublicUrl,
  getPathFromUrl,
  getFileFromStorage,
  deleteFileFromStorage,
  listFilesInFolder,
  withRetry,
  isRetryableError,
  isValidStorageUrl
} from './storage-service';

// Export cleanup service
export { cleanupOrphanedFile } from './cleanup-service';

/**
 * Main entry point for uploading a paper file with validation and security
 * @param file File to upload
 * @returns Public URL of the uploaded file or null if upload failed
 */
export async function uploadPaperFile(file: File): Promise<string | null> {
  try {
    if (!file) {
      console.error('No file provided for upload');
      return null;
    }
    
    // Check if the user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Authentication required', {
        description: 'You must be logged in to upload files'
      });
      return null;
    }
    
    // Perform file validation
    const validation = validatePaperFile(file);
    if (!validation.valid) {
      toast.error('Invalid file', {
        description: validation.message
      });
      return null;
    }
    
    // Check rate limiting
    const isWithinRateLimit = await checkUploadRateLimit(user.id);
    if (!isWithinRateLimit) {
      return null;
    }
    
    // Verify storage bucket exists
    const bucketExists = await verifyStorageBucket();
    if (!bucketExists) {
      return null;
    }
    
    // Get security token for upload
    const securityData = await getSecurityToken(user.id);
    if (!securityData) {
      return null;
    }
    
    // Upload file to storage with retry
    const fileUrl = await withRetry(async () => 
      await uploadFileToStorage(file, user.id, securityData.token, securityData.timestamp)
    );
    
    if (!fileUrl) {
      return null;
    }
    
    return fileUrl;
    
  } catch (error) {
    console.error('Unexpected error uploading file:', error);
    toast.error('An unexpected error occurred during file upload');
    
    // Keep error logging for debugging
    adminError('File upload failed:', { 
      error: error instanceof Error ? error.message : 'Unknown error',
      fileSize: file?.size || 0,
      fileType: file?.type || ''
    });
    
    return null;
  }
}
